'use client';

import React, { useEffect, useState } from 'react';

import cn from 'classnames';
import { usePathname } from 'next/navigation';

import { checkRouteMatch } from '@/helpers/utils/common'; // adjust path if needed

import { useDashboardProvider } from '../DashboardProvider';
import { useDashboardSidebarProvider } from '../DashboardSidebarProvider';
import SidebarLink from './Sidebarlink';
import TooltipRendererWrapper from './TooltipRendererWrapper';

export type MenuItem = {
  name: string;
  icon?: React.ReactNode;
  link?: string;
  target?: string;
  as?: 'Link' | 'a';
  subMenu?: MenuItem[];
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
};

interface SidebarItemProps {
  item: MenuItem;
  isChild?: boolean;
  depth?: number;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  target?: string;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const SidebarItem = ({ item, isChild = false, depth = 0, onClick, target = '_self' }: SidebarItemProps) => {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  const { activeTitle, setActiveTitle } = useDashboardSidebarProvider();
  const { toggleMenu, setToggleMenu } = useDashboardProvider();

  const hasChildren = !!item.subMenu?.length;

  // Updated match flags
  const { isExactMatch, isChildActive, highlightThisLevel } = checkRouteMatch(item, pathname);

  const isLeafMatch = isExactMatch;
  const isTopLevelMatch = highlightThisLevel || isLeafMatch;

  useEffect(() => {
    if (hasChildren && isChildActive) setOpen(true);
  }, [pathname]);

  useEffect(() => {
    if (toggleMenu) setOpen(false);
  }, [toggleMenu]);

  const fillColor = isTopLevelMatch ? '#fff' : open && hasChildren ? '#154734' : '#101828';

  const iconWithClass = React.isValidElement(item.icon)
    ? React.cloneElement(item.icon, {
        ...(typeof item.icon.type === 'function' ? { fill: fillColor } : {}),
      })
    : null;

  const dynamicPadding = `${Math.max(14 * (depth - 1), 0) + 30}px`;
  const hrefValue = toggleMenu ? '#' : item.link || '#';

  return (
    <li>
      <TooltipRendererWrapper
        title={
          <span className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-xs v2-text-[#101828]">{item.name}</span>
        }
        showTooltip={toggleMenu}
      >
        <SidebarLink
          href={hrefValue}
          as={item.as}
          onClick={e => {
            if (toggleMenu) return setToggleMenu(false);

            if (item.name === 'EZ Ship Manager') {
              localStorage.setItem('active-ez-ship-manager', 'true');
            } else if (item.name === 'EZ Ships') {
              localStorage.setItem('active-ez-ship-manager', 'false');
            }

            onClick?.(e);
            item.onClick?.(e);

            if (hasChildren) setOpen(prev => !prev);
            else setActiveTitle(item.name);
          }}
          target={item.target || target}
          className={cn(
            'v2-flex v2-items-center v2-justify-between v2-gap-3 v2-text-sm v2-font-normal v2-font-sweet-sans-pro-semi-bold v2-p-3 v2-rounded-xs hover:v2-bg-[#F2F4F7]',
            {
              'v2-bg-[#154734] hover:!v2-bg-[#154734]': isTopLevelMatch && !isChild,
              'v2-bg-[#F2F4F7]': (isTopLevelMatch && isChild) || (open && hasChildren && !isTopLevelMatch),
              'v2-pl-[30px] !v2-py-0': isChild,
            }
          )}
          style={{
            paddingLeft: isChild ? dynamicPadding : undefined,
          }}
        >
          <span
            className={cn(
              'v2-flex v2-items-center v2-gap-3 v2-transition-all v2-text-balance',
              isChild && 'v2-border-l v2-h-[48px] v2-pl-3',
              isChild && isLeafMatch && '!v2-border-l-[#154734]'
            )}
          >
            {iconWithClass}
            <p
              className={cn('v2-text-[#101828]', {
                'v2-hidden': toggleMenu,
                '!v2-text-white': isTopLevelMatch && !isChild,
                'v2-text-[#154734]': (isTopLevelMatch && isChild) || (open && hasChildren && !isTopLevelMatch),
              })}
            >
              {item.name}
            </p>
          </span>
          {hasChildren && (
            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={cn('v2-transition-all v2-rotate-180', {
                '!v2-rotate-0': open,
              })}
            >
              <path
                d="M18 15.1368L12 9.13678L6 15.1368"
                stroke={isTopLevelMatch && !isChild ? '#fff' : open ? '#154734' : '#101828'}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </SidebarLink>
      </TooltipRendererWrapper>

      {open && hasChildren && (
        <ul>
          {item.subMenu?.map((subItem, idx) => (
            <SidebarItem key={idx} item={subItem} isChild={true} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
};

export default SidebarItem;
